import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import { useMutation, useQuery } from '@apollo/client'
import { Button, List, message, Modal, Space } from 'antd'
import React, { FC, useState } from 'react'
import { translate } from '../../../core'
import { InfoTooltip } from '../../../core/component'
import { DeleteBanner, GetBanners, UpdateBannerPriority } from '../graphql/website-settings.graphql'
import { Banner } from '../type/website-settings'
import { BannerEntryForm } from './BannerEntryForm'
import { DraggableList, DraggableListItem, DragListItemHandle } from '../../../core/component/DraggableList'
import { arrayMove } from '@dnd-kit/sortable'
import { DragEndEvent } from '@dnd-kit/core'

export const BannerList: FC = () => {
  const [messageApi, contextHolder] = message.useMessage()
  const { data, loading } = useQuery(GetBanners)
  const [open, setOpen] = useState(false)
  const [banner, setBanner] = useState<Banner>()
  const [banners, setBanners] = useState<(Banner & { key: string })[]>([])

  React.useEffect(() => {
    if (data?.adminWebsiteSettings.banners) {
      setBanners(data.adminWebsiteSettings.banners.map(banner => ({
        ...banner,
        key: banner.id.toString()
      })))
    }
  }, [data])

  const [deleteBanner, { loading: deleting }] = useMutation(DeleteBanner, {
    onCompleted: () => messageApi.success(translate('banner_delete_success_message')),
    onError: error => messageApi.error(error.message),
    refetchQueries: [GetBanners],
  })

  const [updateBannerPriority] = useMutation(UpdateBannerPriority, {
    onCompleted: () => messageApi.success(translate('banner_update_priority_success_message')),
    onError: error => messageApi.error(error.message),
    refetchQueries: [GetBanners],
  })

  const onDragEnd = async ({active, over}: DragEndEvent) => {
    if (active.id === over?.id)
      return

    setBanners((prevState) => {
      const activeIndex = prevState.findIndex((banner) => banner.key === active?.id)
      const overIndex = prevState.findIndex((banner) => banner.key === over?.id)
      const newItems = arrayMove(prevState, activeIndex, overIndex)

      updateBannerPriority({
        variables: {
          input: newItems.map(({id}, index) => ({
            id,
            priority: index,
          }))
        }
      })

      return newItems
    })
  }

  return (
    <React.Fragment>
      <Space size={8} align='center'>
        <Button
          type='primary'
          icon={<PlusOutlined />}
          onClick={() => {
            setOpen(true)
            setBanner(undefined)
          }}
        >
          {translate('create', 'banner')}
        </Button>
        <InfoTooltip contentKey='banner.list' placement='right' />
      </Space>

      <DraggableList
        items={banners}
        onDragEnd={onDragEnd}
      >
        <List
          className='mt-3 w-50 bg-white'
          bordered
          size='small'
          loading={loading}
          dataSource={banners}
          renderItem={item => (
            <DraggableListItem id={item.key}>
              <List.Item
                actions={[
                  <Button
                    key="edit"
                    type='link'
                    shape='circle'
                    icon={<EditOutlined />}
                    onClick={() => {
                      setOpen(true)
                      setBanner(item)
                    }}
                  />,
                  <Button
                    key="delete"
                    loading={deleting}
                    type='text'
                    shape='circle'
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => {
                      Modal.confirm({
                        title: translate(
                          "delete",
                          "banner",
                          `#${item.id}`
                        ),
                        content: translate("sure_message"),
                        onOk: () => deleteBanner({
                          variables: {
                            input: {
                              id: item.id
                            }
                          }
                        }),
                      })
                    }}
                  />
                ]}
              >
                <List.Item.Meta
                  className='d-flex align-items-center'
                  avatar={<DragListItemHandle key="drag" />}
                  title={`${item.name}`}
                  description={
                    <div>
                      <div>{translate('location')}: {item.location}</div>
                    </div>
                  }
                />
              </List.Item>
            </DraggableListItem>
          )}
        />
      </DraggableList>

      <BannerEntryForm
        onClose={() => setOpen(false)}
        open={open}
        banner={banner}
      />
      {contextHolder}
    </React.Fragment>
  )
}